import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import EmailCollection from './components/EmailCollection';
import TekyuLogo from './TekyuLogo';
import { buildBootcampUrl } from './utils/leadCapture';
import tekyuProjects from './data/tekyuProjects';
import { Sparkles, ArrowRight, CheckCircle2, Users, TrendingUp, Zap } from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const bootcampUrl = buildBootcampUrl({ utmMedium: 'landing', utmCampaign: 'bootcamp' });

  // Get first 3 projects for preview
  const featuredProjects = tekyuProjects.slice(0, 3);

  return (
    <div className="min-h-screen app-shell app-ambient">
      <div className="app-content mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <TekyuLogo ariaLabel="Tekyu - Your Free Personal Tech Roadmap and Project Builder" className="h-20 sm:h-24 w-auto mx-auto mb-6 drop-shadow-[0_12px_24px_rgba(0,0,0,0.35)]" />
          <h1 className="text-5xl sm:text-6xl font-extrabold tracking-tight mb-4">
            Your Free Personal Tech Roadmap<br />
            <span className="text-[color:var(--primary)]">and Project Builder</span>
          </h1>
          <p className="mt-6 text-xl sm:text-2xl text-[color:var(--muted)] max-w-3xl mx-auto mb-8">
            Get a personalized learning path for DevSecOps, Cloud Security, SRE, and more. 
            Plan your learning, practise with projects, and prepare evidence of your skills.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link
              to="/roles"
              className="inline-flex items-center justify-center rounded-xl px-8 py-4 font-bold text-lg transition-all bg-[color:var(--primary)] text-slate-950 hover:opacity-90 shadow-lg shadow-green-500/25"
            >
              Get Your Free Roadmap
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <a
              href={bootcampUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center rounded-xl px-8 py-4 font-bold text-lg border-2 transition-all bg-transparent border-indigo-500 text-indigo-300 hover:bg-indigo-500/10"
            >
              <Sparkles className="mr-2 h-5 w-5" />
              Join Cyber Agoge Bootcamp
            </a>
          </div>

          {/* Social Proof */}
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-[color:var(--muted)]">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              <span>Choose a path at your level</span>
            </div>
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              <span>Build a practical portfolio</span>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4" />
              <span>100% Free roadmap</span>
            </div>
          </div>
        </div>

        {/* Featured Projects Section */}
        <div className="mb-16">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-3">
              Build Projects You Can Explain
            </h2>
            <p className="text-lg text-[color:var(--muted)] max-w-2xl mx-auto">
              Hands-on security projects with step-by-step guidance. Each project builds real skills you can showcase to employers.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {featuredProjects.map((project) => (
              <div
                key={project.id}
                className="rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-950/60 to-slate-900/40 p-6 hover:border-green-500/30 transition-all hover:shadow-lg hover:shadow-green-500/10"
              >
                <div className="text-4xl mb-3">{project.icon}</div>
                <h3 className="text-xl font-bold text-white mb-2">{project.title}</h3>
                <p className="text-sm text-slate-400 mb-4">{project.subtitle}</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {project.skills.slice(0, 3).map((skill, idx) => (
                    <span key={idx} className="text-xs px-2 py-1 rounded bg-slate-800 text-slate-300">
                      {skill}
                    </span>
                  ))}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">
                    {project.estimatedHours} hours • {project.difficulty}
                  </span>
                  <Link
                    to="/portal"
                    className="text-sm font-semibold text-green-400 hover:text-green-300 flex items-center gap-1"
                  >
                    View Project
                    <ArrowRight className="h-3 w-3" />
                  </Link>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/portal"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold bg-gradient-to-r from-indigo-500 to-purple-500 text-white hover:opacity-90"
            >
              View All Projects
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>

        {/* Why Tekyu Section */}
        <div className="mb-16">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-center text-white mb-12">
            Why Choose Tekyu?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: <CheckCircle2 className="h-8 w-8 text-green-400" />,
                title: '100% Free Roadmap',
                description: 'Get your personalized career roadmap instantly. No credit card required.',
              },
              {
                icon: <Sparkles className="h-8 w-8 text-purple-400" />,
                title: 'Guided Projects',
                description: 'Explore paid project guides with step-by-step explanations and practical tasks.',
              },
              {
                icon: <TrendingUp className="h-8 w-8 text-blue-400" />,
                title: 'Job-Ready Skills',
                description: 'Practise technical skills and build projects you can discuss in interviews.',
              },
            ].map((feature, idx) => (
              <div
                key={idx}
                className="rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-950/60 to-slate-900/40 p-6 text-center"
              >
                <div className="flex justify-center mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-slate-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Bootcamp CTA - Prominent */}
        <div className="mb-16">
          <div className="rounded-2xl border-2 border-indigo-500/50 bg-gradient-to-br from-indigo-950/60 to-purple-950/40 p-8 sm:p-12 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-indigo-500/10 animate-pulse" />
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-500/20 border border-indigo-500/30 mb-4">
                <Sparkles className="h-4 w-4 text-indigo-300" />
                <span className="text-sm font-bold text-indigo-300">CYBER AGOGE BOOTCAMP</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">
                Want Structured Guidance from Industry Leaders?
              </h2>
              <p className="text-lg text-slate-300 mb-2 max-w-2xl mx-auto">
                Join the Cyber Agoge Bootcamp for live teaching, guided practicals and project support.
              </p>
              <p className="text-base text-indigo-300 font-semibold mb-6">
                New to tech or already experienced: choose support that fits your starting point.
              </p>
              <a
                href={bootcampUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-lg transition-all bg-gradient-to-r from-indigo-500 to-purple-500 text-white hover:opacity-90 shadow-lg shadow-indigo-500/25"
              >
                Learn More About Cyber Agoge Bootcamp
                <ArrowRight className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>

        {/* Quick Start CTA */}
        <div className="mb-16 text-center">
          <h2 className="text-3xl font-extrabold text-white mb-4">
            Ready to Start Your Tech Career?
          </h2>
          <p className="text-lg text-[color:var(--muted)] mb-6">
            Get your free personalised roadmap in minutes. Enter your email to open it; marketing updates are optional.
          </p>
          <Link
            to="/roles"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-lg transition-all bg-[color:var(--primary)] text-slate-950 hover:opacity-90 shadow-lg shadow-green-500/25"
          >
            Choose Your Career Path
            <ArrowRight className="h-5 w-5" />
          </Link>
        </div>

        {/* Email Collection */}
        <div id="email-updates" className="w-full max-w-2xl mx-auto mb-12">
          <EmailCollection />
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
